// src/components/MobileDetailView.jsx
import React from 'react';
import { ChevronLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

/**
 * A mobile-optimized layout for detail views
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Main content
 * @param {React.ReactNode} props.header - Header content (title, etc.)
 * @param {React.ReactNode} props.headerActions - Actions to show in the header (optional)
 * @param {React.ReactNode} props.footer - Footer content (optional)
 * @param {string} props.backUrl - URL to navigate back to (optional)
 * @param {string} props.backLabel - Label for the back button (optional)
 * @param {string} props.className - Additional CSS classes for the container
 * @param {string} props.contentClassName - Additional CSS classes for the content container
 */
const MobileDetailView = ({
  children,
  header,
  headerActions,
  footer,
  backUrl,
  backLabel = 'Back',
  className = '',
  contentClassName = '',
}) => {
  const navigate = useNavigate();
  
  const handleBack = () => {
    if (backUrl) {
      navigate(backUrl);
    } else {
      navigate(-1); // Go back in history
    }
  };

  return (
    <div className={`mobile-detail-container w-full ${className}`} style={{width: '100%', padding: 0, marginTop: '8px'}}>
      {/* Back navigation - shown prominently on mobile */}
      <div className="mb-2 sm:mb-3 px-4">
        <button
          onClick={handleBack}
          className="flex items-center text-sm sm:text-base text-slate-600 hover:text-slate-800 mobile-touch-target py-2"
          aria-label={`Go back to ${backLabel}`}
        >
          <ChevronLeft size={20} className="mr-1" />
          <span>{backLabel}</span>
        </button>
      </div>
      
      {/* Header section with title and actions */}
      {header && (
        <div className="mb-4 sm:mb-6 px-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex-1">
              {header}
            </div>
            
            {headerActions && (
              <div className="flex items-center justify-start sm:justify-end gap-2 flex-wrap">
                {headerActions}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Main content */}
      <div className={`${contentClassName}`}>
        {children}
      </div>
      
      {/* Footer section */}
      {footer && (
        <div className="mt-6 sm:mt-8 mb-4 sm:mb-6 px-4">
          {footer}
        </div>
      )}
    </div>
  );
};

export default MobileDetailView;